import { Link } from "react-router-dom";
import {
  Sun, Users, ShieldCheck, Clock, BadgeCheck, ArrowRight,
} from "lucide-react";
import ProjectCard from "@/components/ProjectCard";

const HERO_IMG = "/img/hero-solar-construction.png";

export function HomeHero() {
  return (
    <section className="ye-hero" style={{ backgroundImage: `url(${HERO_IMG})` }} data-testid="home-hero">
      <div className="ye-container">
        <div style={{ maxWidth: 880 }}>
          <span className="ye-eyebrow">Solar & BESS I&amp;C • EPC • Electrical Contracting</span>
          <h1 className="ye-display ye-h1" style={{ margin: "8px 0 22px" }}>From Piling to Power-On.</h1>
          <p style={{ fontSize: "clamp(1.05rem, 1.5vw, 1.25rem)", lineHeight: 1.6, margin: "0 0 30px", color: "rgba(255,255,255,0.92)" }}>
            Yaduraj Enterprises is an asset-backed installation &amp; commissioning partner for solar and battery energy storage projects. We mobilise our own piling rigs, earthmoving machines and licensed electrical teams — so your schedule never waits on a rental yard.
          </p>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            <Link to="/contact" className="ye-btn ye-btn-primary" data-testid="hero-cta-quote">Get a Quote <ArrowRight size={16} /></Link>
            <Link to="/projects" className="ye-btn ye-btn-outline-white" data-testid="hero-cta-projects">View Our Projects</Link>
          </div>
          <div className="ye-chip" data-testid="hero-chip"><Sun size={14} /> Powering a Sustainable Future</div>
        </div>
      </div>
    </section>
  );
}

export function TrustStrip() {
  return (
    <section className="ye-trust">
      <div className="ye-container ye-trust-grid">
        <div className="ye-trust-item"><BadgeCheck size={22} /> Quality Work</div>
        <div className="ye-trust-item"><Clock size={22} /> Timely Execution</div>
        <div className="ye-trust-item"><Users size={22} /> Experienced Team</div>
        <div className="ye-trust-item"><ShieldCheck size={22} /> Reliable Service</div>
      </div>
    </section>
  );
}

export function StatsBand() {
  const stats = [
    { num: "8+", label: "Owned heavy machines & rigs" },
    { num: "3-in-1", label: "Civil, electrical & T&C under one team" },
    { num: "Licensed", label: "Electrical contractor–led execution" },
    { num: "2 Offices", label: "Barmer (Rajasthan) & New Delhi" },
  ];
  return (
    <section className="ye-stats" data-testid="stats-band">
      <div className="ye-container ye-stats-grid">
        {stats.map((s) => (
          <div key={s.num}>
            <div className="ye-stat-num">{s.num}</div>
            <div className="ye-stat-label">{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function ServicesGrid({ services }) {
  return (
    <section className="ye-section ye-section--bg">
      <div className="ye-container">
        <div style={{ textAlign: "center", marginBottom: 44 }}>
          <span className="ye-eyebrow">What We Do</span>
          <h2 className="ye-display ye-h2" style={{ margin: "6px 0 0" }}>Services Built Around Project Schedules</h2>
        </div>
        <div className="ye-grid-4">
          {services.map((s) => {
            const Icon = s.icon;
            return (
              <div className="ye-card" key={s.title} data-testid={`home-service-${s.title.toLowerCase().replace(/\s|&/g, "-")}`}>
                <div style={{ width: 48, height: 48, borderRadius: 8, background: "rgba(245,130,32,0.10)", color: "var(--orange)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
                  <Icon size={24} />
                </div>
                <h3 className="ye-h3" style={{ margin: "0 0 8px" }}>{s.title}</h3>
                <p style={{ margin: "0 0 14px", fontSize: 14.5, lineHeight: 1.55 }}>{s.blurb}</p>
                <Link to={`/services${s.anchor}`} style={{ fontWeight: 600, fontSize: 14, color: "var(--orange)" }}>Learn more →</Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function FeaturedProjects({ featured }) {
  return (
    <section className="ye-section">
      <div className="ye-container">
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <span className="ye-eyebrow">Recent Work</span>
          <h2 className="ye-display ye-h2" style={{ margin: "6px 0 0" }}>Projects That Held Their Schedules</h2>
        </div>
        <div className="ye-grid-3" style={{ gap: 28 }}>
          {featured.map((p) => <ProjectCard key={p.slug} project={p} />)}
        </div>
        <div style={{ textAlign: "center", marginTop: 36 }}>
          <Link to="/projects" className="ye-btn ye-btn-outline-navy" data-testid="home-all-projects">See All Projects →</Link>
        </div>
      </div>
    </section>
  );
}

const ADVANTAGES = [
  { t: "We own the fleet.", b: "No rental dependence, no mobilisation roulette. Our JCBs, piling rigs, auger and concrete mixer move when your project needs them — not when a rental yard frees up." },
  { t: "Licensed electrical depth.", b: "Led by an expert electrical contractor, our teams handle LT/HT works, terminations, SVG installation and harmonic solutions — backed by proper test records." },
  { t: "Desert-proven execution.", b: "Headquartered in Sheo, Barmer — the heart of Rajasthan's solar belt. DTH rigs for hard strata, and our own site campers for self-sufficient mobilisation at remote locations." },
  { t: "Documentation discipline.", b: "Megger/IR test reports, pre-commissioning checklists and orderly handover files — the paperwork your commissioning team actually needs." },
];

export function AdvantageGrid() {
  return (
    <section className="ye-section">
      <div className="ye-container">
        <div style={{ textAlign: "center", marginBottom: 44 }}>
          <span className="ye-eyebrow">The Yaduraj Advantage</span>
          <h2 className="ye-display ye-h2" style={{ margin: "6px 0 0" }}>Built to Hold Your Critical Path</h2>
        </div>
        <div className="ye-grid-2" style={{ gap: 32 }}>
          {ADVANTAGES.map((f) => (
            <div key={f.t} className="ye-card" style={{ padding: 30 }}>
              <h3 className="ye-h3" style={{ margin: "0 0 10px" }}>{f.t}</h3>
              <p style={{ margin: 0 }}>{f.b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const FLEET_TEASE = [
  { name: "JCB Backhoe Loader", img: "/img/equipment-jcb-backhoe.png" },
  { name: "DTH Piling Rig", img: "/img/equipment-dth-piling-rig.png" },
  { name: "Hydraulic Auger", img: "/img/equipment-hydraulic-auger.png" },
  { name: "AJAX Concrete Mixer", img: "/img/equipment-ajax-mixer.png" },
];

export function FleetTeaser() {
  return (
    <section className="ye-section ye-section--bg">
      <div className="ye-container">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "end", marginBottom: 32, gap: 16, flexWrap: "wrap" }}>
          <h2 className="ye-display ye-h2" style={{ margin: 0 }}>Our Machines. Your Schedule.</h2>
          <Link to="/equipment" className="ye-btn ye-btn-outline-navy">See the Full Fleet →</Link>
        </div>
        <div className="ye-grid-4">
          {FLEET_TEASE.map((f) => (
            <div className="ye-fleet-card" key={f.name}>
              <div className="ye-fleet-photo"><img loading="lazy" src={f.img} alt={f.name} /></div>
              <div className="ye-fleet-body">
                <h3 className="ye-h3" style={{ fontSize: "1.05rem" }}>{f.name}</h3>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
